import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
    Select, 
    Option,
    Radio,
    Card,
    List,
    ListItem,
    ListItemPrefix,
    Typography,
    Alert 
} from "@material-tailwind/react";
import RecordList from './RecordList';

function Icon() {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        className="h-6 w-6"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
        />
      </svg>
    );
}

/**
 * 1) 백엔드(/api/record-competitions)에서 가져오는 Raw 타입
 *   comp_div_ls: DB에는 "5km,10km" 등 문자열로 있을 수 있음.
 */
type CompetitionRaw = {
    comp_date: string;      // 예: "2024.08", "2023.11", "2022-07", etc.
    comp_title: string;     // 예: "OO마라톤"
    comp_div_ls: string;    // "5km,10km"
    comp_place: string;     // "대전", "광주" ...
    comp_code : number;
  };
  
  /**
   * 2) 실제 RecordList.tsx에서 요구하는 최종 Competition 타입
   *   comp_code: number   (여기서는 DB에 없으니 임의로 0)
   *   comp_link: string   (DB에 없으니 일단 "")
   */
  type Competition = {
    comp_date: string;
    comp_code: number;
    comp_title: string;
    comp_div_ls: string[];
    comp_place: string;
    comp_link: string;
  };
  
  const RecordDate: React.FC = () => {
    // (A) 원본(가공 전) 데이터를 저장할 state
    const [rawDates, setRawDates] = useState<CompetitionRaw[]>([]);
  
    // (B) 최종적으로 필터링/정렬된 데이터를 저장할 state (Competition[])
    const [filteredDates, setFilteredDates] = useState<Competition[]>([]);
  
    // 달(month) / 연도(year) 관련
    const months = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
    const currentYear = new Date().getFullYear().toString();
    const currentMonth = (new Date().getMonth() + 1).toString().padStart(2, '0');
  
    const [selectedYear, setSelectedYear] = useState<string>(currentYear);
    const [selectedMonth, setSelectedMonth] = useState<string>(`${parseInt(currentMonth)}월`);
  
    // 알림 및 라디오 버튼 비활성화
    const [showAlert, setShowAlert] = useState(false);
    const [disableRadio, setDisableRadio] = useState(false);
  
    // ---------------------------
    // 1) 백엔드에서 대회 목록 호출
    // ---------------------------
    useEffect(() => {
      axios.get('/api/record-competitions')
        .then((response) => {
          // response.data = CompetitionRaw[]
          const fetched: CompetitionRaw[] = response.data || [];
  
          // rawDates state에 저장
          setRawDates(fetched);
  
          // 우선 전부 변환해서 filteredDates에 넣어둔다. (초기 상태)
          const initialData: Competition[] = fetched.map((item) => ({
            comp_date: item.comp_date || '',
            comp_code: item.comp_code || 0,
            comp_title: item.comp_title || '',
            comp_div_ls: item.comp_div_ls
              ? item.comp_div_ls.split(',').map(s => s.trim())
              : [],
            comp_place: item.comp_place || '',
            comp_link: ''
          }));
  
          // 날짜 내림차순 정렬 (예: "2024.08" → new Date("2024-08-01"))
          initialData.sort((a, b) => {
            const dateA = convertToDate(a.comp_date);
            const dateB = convertToDate(b.comp_date);
            return dateB.getTime() - dateA.getTime();
          });
  
          setFilteredDates(initialData);
        })
        .catch((error) => {
          console.error("Error fetching record-competitions:", error);
        });
    }, []);
  
    // ---------------------------
    // 2) 연/월 변경 시 필터링 + 정렬
    // ---------------------------
    useEffect(() => {
      // (1) 먼저 rawDates -> Competition[]로 변환
      const allData: Competition[] = rawDates.map((item) => ({
        comp_date: item.comp_date || '',
        comp_code: item.comp_code || 0,
        comp_title: item.comp_title || '',
        comp_div_ls: item.comp_div_ls
          ? item.comp_div_ls.split(',').map(s => s.trim())
          : [],
        comp_place: item.comp_place || '',
        comp_link: ''
      }));
  
      // (2) 연/월 필터
      let filtered = allData;
  
      // 연도 필터
      if (selectedYear) {
        filtered = filtered.filter(d => {
          // "2024.08" → split('.')[0] = "2024"
          // "2023-07" → substring(0,4) = "2023"
          // 혹은 '.' 구분이 없을 수도 있으므로, 상황에 맞게 처리
          if (d.comp_date.includes('.')) {
            const yearStr = d.comp_date.split('.')[0];
            return yearStr === selectedYear;
          } else if (d.comp_date.includes('-')) {
            // 예: "2024-07" or "2024-07-15"
            const yearStr = d.comp_date.substring(0, 4);
            return yearStr === selectedYear;
          }
          // 만약 '2024'만 있다면?
          return d.comp_date.substring(0, 4) === selectedYear;
        });
      }
  
      // 월 필터
      if (selectedMonth) {
        const monthIndex = months.indexOf(selectedMonth) + 1; // 1~12
        const monthString = monthIndex.toString().padStart(2, '0'); // "01" ~ "12"
  
        filtered = filtered.filter(d => {
          // 예: "2024.08" → split('.')[1] = "08"
          //     "2024-07" → substring(5,7) = "07"
          if (d.comp_date.includes('.')) {
            const splitted = d.comp_date.split('.');
            if (splitted.length >= 2 && splitted[1] === monthString) {
              return true;
            }
          } else if (d.comp_date.includes('-')) {
            // "2024-07"
            const mm = d.comp_date.substring(5, 7);
            if (mm === monthString) {
              return true;
            }
          }
          return false;
        });
      }
  
      // (3) 정렬 (내림차순)
      filtered.sort((a, b) => {
        const dateA = convertToDate(a.comp_date);
        const dateB = convertToDate(b.comp_date);
        return dateB.getTime() - dateA.getTime();
      });
  
      setFilteredDates(filtered);
    }, [selectedYear, selectedMonth, rawDates]);
  
    // ---------------------------
    // 연/월 Select 핸들러
    // ---------------------------
    const handleYearChange = (value: string | undefined) => {
      setSelectedYear(value || '');
    };
  
    const handleMonthChange = (month: string) => {
      if (!selectedYear) {
        setShowAlert(true);
        setDisableRadio(true);
        setTimeout(() => {
          setShowAlert(false);
          setDisableRadio(false);
        }, 1500);
        return;
      }
      setSelectedMonth(month);
    };
  
    const handleMonthSelectChange = (value: string | undefined) => {
      handleMonthChange(value || '');
    };
  
    // -------------
    // 유틸: 날짜문자열 → Date 변환
    // -------------
    function convertToDate(str: string): Date {
      // "2024.08" -> "2024-08-01"
      // "2024.08.12" (만약 이런 형태면?) -> "2024-08-12"
      // "2024-07" -> 그냥 new Date("2024-07-01")
      // "2024" -> new Date("2024-01-01")
      if (!str) return new Date('1900-01-01');
      let normalized = str;
  
      // '.' 구분인 경우 1) "YYYY.MM" → "YYYY-MM-01"
      if (normalized.includes('.')) {
        const parts = normalized.split('.');
        if (parts.length === 2) {
          // "YYYY.MM"
          normalized = `${parts[0]}-${parts[1]}-01`;
        } else if (parts.length === 3) {
          // "YYYY.MM.DD"
          normalized = `${parts[0]}-${parts[1]}-${parts[2]}`;
        } else {
          // 이상한 경우 -> 그냥 뒤에 "-01" 붙여서 변환
          normalized = normalized.replace(/\./g, '-') + '-01';
        }
      } else if (/^\d{4}-\d{1,2}$/.test(normalized)) {
        // "YYYY-MM" 인 경우 → 뒤에 '-01'
        normalized += '-01';
      } else if (/^\d{4}$/.test(normalized)) {
        // "YYYY"만 있으면 → '-01-01'
        normalized += '-01-01';
      }
      return new Date(normalized);
    }
  
    return (
      <div>
        <div className='flex justify-center'>
          <div className='flex items-center gap-4 mt-3  lst:flex-col'>
            {/* 연도 선택 */}
            <div className='w-25'>
              <Select size='lg' label="연도" value={selectedYear} onChange={handleYearChange}>
                {/* rawDates에서 연도만 추출하여 중복 제거 */}
                {[...new Set(rawDates.map(r => {
                  // 예: "2024.08" -> "2024"
                  if (r.comp_date.includes('.')) {
                    return r.comp_date.split('.')[0];
                  } else if (r.comp_date.includes('-')) {
                    return r.comp_date.substring(0, 4);
                  }
                  // 기타 케이스
                  return r.comp_date.substring(0, 4);
                }))]
                  .filter(year => year.trim() !== '')
                  .sort()
                  .map((year, index) => (
                    <Option key={index} value={year}>{year}년</Option>
                  ))
                }
              </Select>
            </div>
  
            {/* (모바일) 월 선택 */}
            <div className='w-25 dt:hidden'>
              <Select size='lg' label="월" value={selectedMonth} onChange={handleMonthSelectChange}>
                {months.map((month, index) => (
                  <Option key={index} value={month}>{month}</Option>
                ))}
              </Select>
            </div>
  
            {/* (데스크톱) 월 선택 Radio */}
            <Card className="border-blue-gray-400 border-2 w-[73rem] ph:hidden">
              <List className="flex-row">
                {months.map((month, index) => (
                  <ListItem key={index} className="p-0">
                    <label
                      htmlFor={`horizontal-list-${month}`}
                      className="flex w-full cursor-pointer items-center px-3 py-2"
                    >
                      <ListItemPrefix className="mr-3">
                        <Radio
                          name="horizontal-list"
                          id={`horizontal-list-${month}`}
                          ripple={false}
                          className="hover:before:opacity-0"
                          containerProps={{ className: "p-0" }}
                          checked={selectedMonth === month}
                          onChange={() => handleMonthChange(month)}
                          disabled={disableRadio} 
                        />
                      </ListItemPrefix>
                      <Typography
                        color="blue-gray"
                        className="font-medium text-blue-gray-400"
                      >
                        {month}
                      </Typography>
                    </label>
                  </ListItem>
                ))}
              </List>
            </Card>
          </div>
        </div>
  
        {/* 연도 미선택 시 알림 모달 */}
        {showAlert && (
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <Alert icon={<Icon />} className='w-80 bg-blue-gray-400'>
              연도를 먼저 선택해주세요
            </Alert>
          </div>
        )}
  
        {/* 필터된 데이터 렌더 */}
        <div className='flex justify-center'>
          <RecordList dates={filteredDates} />
        </div>
      </div>
    );
  };
  
  export default RecordDate;

//export default RecordDate;
